import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import { toast } from 'react-toastify';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
  ConfirmDialog,
} from '../../components/ui';
import type { Column, ActionItem } from '../../components/ui';
import { servicesApi } from '../../api/services';
import { PAGE_SIZE } from '../../constants';
import { formatDate, paginate, matchesSearch } from '../../utils';
import type { AccountStatus, ServiceCategory } from '../../types';

interface CategoryForm {
  name: string;
  description: string;
  status: AccountStatus;
}

const emptyForm: CategoryForm = {
  name: '',
  description: '',
  status: 'Active',
};

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ServiceCategory | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [confirm, setConfirm] = useState<{
    category: ServiceCategory;
    status: AccountStatus;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await servicesApi.getAllCategories();
      setRows(data);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () => rows.filter((row) => matchesSearch(row.name, search)),
    [rows, search]
  );

  const paged = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (category: ServiceCategory) => {
    setEditing(category);
    setForm({
      name: category.name,
      description: category.description,
      status: category.status,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      toast.info('Category create/update API is not available yet');
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      toast.info('Category status API is not available yet');
      setConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns: Column<ServiceCategory>[] = [
    {
      id: 'icon',
      label: 'Icon',
      minWidth: 72,
      render: (row) => (
        <Box
          onClick={() => navigate(`/categories/${row.id}`)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {row.icon}
        </Box>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      minWidth: 160,
      render: (row) => (
        <Typography
          fontWeight={600}
          fontSize={14}
          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          onClick={() => navigate(`/categories/${row.id}`)}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: 'providers',
      label: 'Providers',
      align: 'right',
      render: (row) => row.providerCount,
    },
    {
      id: 'requests',
      label: 'Requests',
      align: 'right',
      render: (row) => row.requestCount,
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'created',
      label: 'Created',
      render: (row) => formatDate(row.createdAt),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => {
        const actions: ActionItem[] = [
          {
            label: 'View',
            icon: <VisibilityIcon fontSize="small" />,
            onClick: () => navigate(`/categories/${row.id}`),
          },
          {
            label: 'Edit',
            icon: <EditIcon fontSize="small" />,
            onClick: () => openEdit(row),
          },
          row.status === 'Active'
            ? {
                label: 'Deactivate',
                icon: <BlockIcon fontSize="small" />,
                danger: true,
                onClick: () => setConfirm({ category: row, status: 'Inactive' }),
              }
            : {
                label: 'Activate',
                icon: <CheckCircleOutlineIcon fontSize="small" />,
                onClick: () => setConfirm({ category: row, status: 'Active' }),
              },
        ];
        return <ActionMenu actions={actions} />;
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Categories"
        subtitle="Manage service categories and availability"
        actions={
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={openCreate}>
            Add Category
          </Button>
        }
      />

      <PageFilters
        search={search}
        searchPlaceholder="Search by name…"
        onSearchChange={setSearch}
      />

      <DataTable
        columns={columns}
        rows={paged}
        loading={loading}
        getRowId={(row) => row.id}
        emptyTitle="No categories found"
        emptyDescription="Try a different search or add a new category."
      />

      <TablePaginationBar
        page={page}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onChange={setPage}
      />

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value as AccountStatus }))
              }
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
            {editing ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.status === 'Active' ? 'Activate Category' : 'Deactivate Category'}
        message={
          confirm
            ? `Are you sure you want to ${confirm.status === 'Active' ? 'activate' : 'deactivate'} “${confirm.category.name}”?`
            : ''
        }
        confirmLabel={confirm?.status === 'Active' ? 'Activate' : 'Deactivate'}
        danger={confirm?.status === 'Inactive'}
        loading={confirmLoading}
        onConfirm={handleStatusChange}
        onClose={() => !confirmLoading && setConfirm(null)}
      />
    </Box>
  );
};

export default CategoriesPage;
